import { useCallback, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { DateTime } from 'luxon';
import { ApolloCache, FetchResult } from '@apollo/client';
import {
  LabelType,
  LabelValueAcceptedBy,
  LabelValueSource,
  MergeTimeSignalsMutation,
  PutLabelSuggestionsMutation,
  SortOrder,
  TimeSignal,
  TimeSignalAcceptedBy,
  TimeSignalClassification,
  TimeSignalsDocument,
  TimeSignalSource,
  TimeSignalSourcesInput,
  TimeSignalsQuery,
  UpdateTimeSignalMutation,
  usePutLabelSuggestionsMutation
} from 'graphql/types';
import { useNotesSelectionReset } from 'modules/notes/list/hooks';
import {
  FormTimesheetLabel,
  TimesheetLabel
} from 'modules/timesheet-labels/labelSchema';
import { useNotesToastContext } from 'modules/notes/NotesToastContextProvider';
import { mapDateToISOString } from 'utils/date-time';
import { useIsSmartSuggestionSettingEnabled } from 'modules/timesheet-labels/hooks/useIsSmartSuggestionSettingEnabled';
import { useMergeTimeSignalsMutation } from 'graphql/types';
import {
  NotesClassification,
  NOTES_PAGE_TYPE,
  useNotesFilterContext
} from 'modules/notes/NotesFilterContextProvider';
import {
  TimeSignalGetParam,
  useTimeSignalGetParam
} from 'modules/notes/hooks/useTimeSignalParam';
import { TIMELINE_TIMESIGNAL_COUNT } from 'modules/notes/Timeline';
import { SearchCriteria } from 'modules/notes/common';
import {
  updateCachedTimeSignalsWithFederatedSuggestions,
  updateCacheOnMerge,
  updateCreateSignalCache
} from 'modules/notes/cache/utils/cache-utils';
import { mapTimesheetLabel, truncateMessage } from 'modules/notes/common/utils';
import { useNotesSortContext } from 'modules/notes/NotesSortContextProvider';
import { useNotesAnimationContext } from 'modules/notes/providers';
import { NotesActions } from 'modules/notes/notesSelector/hooks';
import { clearCachedDates } from 'modules/notes/cache/utils/time-signal-utils';
import { useMeContext } from 'modules/me/MeContextProvider';
import { useFeatureFlag } from 'modules/common/hooks';
import { shouldAcceptSuggestion } from '../../../common/utils/shouldAcceptSuggestion';

export const updateCache = (
  param: TimeSignalGetParam,
  selectedClassification: NotesClassification,
  classificationTarget: TimeSignalClassification,
  searchCriteria: SearchCriteria,
  sortOrder: SortOrder
) => (
  cache: ApolloCache<MergeTimeSignalsMutation>,
  mutationResult: FetchResult<
    MergeTimeSignalsMutation,
    Record<string, any>,
    Record<string, any>
  >
): void => {
  if (mutationResult?.data?.mergeTimeSignals?.timeSignal) {
    updateCreateSignalCache(
      cache,
      mutationResult?.data?.mergeTimeSignals?.timeSignal,
      param,
      selectedClassification,
      searchCriteria,
      sortOrder
    );
  }
  if (mutationResult?.data?.mergeTimeSignals?.mergedTimeSignals) {
    const mergedSignalIds = mutationResult?.data?.mergeTimeSignals?.mergedTimeSignals.map(
      (t) => t.id
    );
    const existingSignals: TimeSignalsQuery | null = cache.readQuery({
      query: TimeSignalsDocument,
      variables: param
    });
    if (existingSignals) {
      const updatedSignals = (existingSignals!.timeSignals.items || []).filter(
        (t) => !mergedSignalIds.includes(t.id)
      );
      cache.writeQuery({
        query: TimeSignalsDocument,
        variables: param,
        data: {
          timeSignals: {
            __typename: 'TimeSignalsResult',
            items: updatedSignals,
            totalCount:
              existingSignals!.timeSignals.totalCount - mergedSignalIds.length,
            cursor: existingSignals!.timeSignals?.cursor
          }
        }
      });
    }
  }
};

export const updateCacheWithMergeUpdate = (
  param: TimeSignalGetParam,
  selectedClassification: NotesClassification,
  classificationTarget: TimeSignalClassification,
  searchCriteria: SearchCriteria,
  sortOrder: SortOrder
) => (
  cache: ApolloCache<MergeTimeSignalsMutation>,
  mutationResult: FetchResult<
    MergeTimeSignalsMutation,
    Record<string, any>,
    Record<string, any>
  >
): void => {
  if (
    mutationResult?.data?.mergeTimeSignals?.timeSignal &&
    mutationResult?.data?.mergeTimeSignals?.mergedTimeSignals
  ) {
    updateCacheOnMerge(
      cache,
      mutationResult?.data?.mergeTimeSignals?.timeSignal,
      mutationResult?.data?.mergeTimeSignals?.mergedTimeSignals,
      param,
      selectedClassification,
      searchCriteria,
      sortOrder
    );
  }
};

const updateCacheWithFederatedSuggestions = (param: TimeSignalGetParam) => (
  cache: ApolloCache<UpdateTimeSignalMutation>,
  mutationResult: FetchResult<PutLabelSuggestionsMutation>
): void => {
  if (mutationResult?.data?.putLabelSuggestions) {
    updateCachedTimeSignalsWithFederatedSuggestions(
      cache,
      mutationResult?.data?.putLabelSuggestions,
      param
    );
  }
};

const acceptSuggestion = (
  labels: TimesheetLabel[],
  isTimeSignalFederatedSuggestionsEnabled: boolean,
  isEmptyLabelsEnabled: boolean
): TimesheetLabel[] => {
  const labelTypesToIgnore = [LabelType.Comment];
  return labels.map((label) => {
    if (shouldAcceptSuggestion(label, isEmptyLabelsEnabled)) {
      if (labelTypesToIgnore.includes(label.type)) {
        return label;
      }

      label.value = label.suggestedValues?.[0];
    }
    if (
      isTimeSignalFederatedSuggestionsEnabled &&
      label.valueSource === LabelValueSource.Suggestion &&
      !label.acceptedBy
    ) {
      // Backwards compatibility: If federatedSuggestions is enabled there could be a legacy suggestion at label.value that is invisible to the user.
      // however the visible federated suggestion the user sees and meant to accept is at label.suggestedValues[0]
      // So if there is a non accepted label.value and federatedSuggestions is enabled then we overwrite it.
      label.value = label.suggestedValues?.[0];
    }
    label.acceptedBy = LabelValueAcceptedBy.User;
    return label;
  });
};

const getMessageId = (
  selectedFolder: NotesClassification,
  targetClassification: TimeSignalClassification
) => {
  return selectedFolder === 'All' ||
    selectedFolder.toLocaleLowerCase() ===
      targetClassification.toLocaleLowerCase()
    ? 'note.merge.mergedTo.common'
    : `note.merge.mergedTo.${targetClassification.toLocaleLowerCase()}`;
};
export const useMergeAction = ({
  notes,
  getValues
}: {
  notes: TimeSignal[];
  getValues: UseFormReturn['getValues'];
}): {
  mergeAction: (
    onNoteDiscard: () => void,
    classificationTarget: TimeSignalClassification
  ) => void;
  isActionInProgress: boolean;
} => {
  const isTimeSignalFederatedSuggestionsEnabled = useFeatureFlag(
    'isTimeSignalFederatedSuggestionsEnabled',
    false
  );
  const isEmptyLabelsEnabled = useFeatureFlag('isEmptyLabelsEnabled', false);

  const me = useMeContext();
  const { setToastContext } = useNotesToastContext();
  const [saving, setSaving] = useState(false);
  const { resetForm } = useNotesSelectionReset();
  const [mergeTimeSignals, { loading }] = useMergeTimeSignalsMutation();
  const [
    putSuggestions,
    { loading: putSuggestionsLoading }
  ] = usePutLabelSuggestionsMutation();
  const isSmartSuggestionEnabled = useIsSmartSuggestionSettingEnabled();
  const { sortOrder } = useNotesSortContext();

  const {
    filterContext: { selectedClassification, searchCriteria, pageType }
  } = useNotesFilterContext();
  const { setNotesToAnimate } = useNotesAnimationContext();
  const { id } = useMeContext() || {};

  const param = useTimeSignalGetParam(
    pageType === NOTES_PAGE_TYPE.TIMELINE
      ? TIMELINE_TIMESIGNAL_COUNT
      : undefined
  );

  const mergeAction = useCallback(
    async (
      onNoteDiscard: () => void,
      classificationTarget: TimeSignalClassification
    ) => {
      setSaving(true);
      const values = getValues();

      const { timesheetLabels, customLabels } = values;
      const tsLabels = ((timesheetLabels ?? []) as FormTimesheetLabel[]).map(
        (l) => l.label
      );
      let labelValues = ([
        ...tsLabels,
        ...(customLabels ?? [])
      ] as TimesheetLabel[]).filter((l) =>
        isTimeSignalFederatedSuggestionsEnabled ? true : l.value
      );

      if (
        isTimeSignalFederatedSuggestionsEnabled &&
        classificationTarget === TimeSignalClassification.Accepted
      ) {
        labelValues = acceptSuggestion(
          labelValues,
          isTimeSignalFederatedSuggestionsEnabled,
          isEmptyLabelsEnabled
        );
      }

      try {
        clearCachedDates(id);
        const targetId = values.id;

        const secondaryNotesIds = notes
          .filter((note) => note.id !== targetId)
          .map((note) => note.id);
        const mergeNotesIds = [targetId, ...secondaryNotesIds];

        // Merge sources
        const notesSources = notes.map((note) => note.sources).flat();
        const mergedSources: TimeSignalSourcesInput[] = [
          ...Array.from(
            new Map(notesSources.map((item) => [item.id, item])).values()
          )
        ].map((item: TimeSignalSource) => ({
          id: item.id,
          name: item.name,
          ref: item.ref,
          image: item.image?.src
            ? {
                src: item.image?.src
              }
            : null
        })) as TimeSignalSourcesInput[];

        const result = await mergeTimeSignals({
          variables: {
            input: {
              ids: mergeNotesIds,
              timeSignal: {
                date: values.date
                  ? DateTime.fromJSDate(values.date).toISODate()
                  : null,
                classification: classificationTarget,
                description: values.note,
                startTime: mapDateToISOString(values.startTime),
                endTime: mapDateToISOString(values.endTime),
                duration: values.duration ? values.duration : null,
                labelValues: labelValues.map((l) =>
                  mapTimesheetLabel(isSmartSuggestionEnabled, l)
                ),
                labelValuesInferenceId: values.labelValuesInferenceId,
                sources: mergedSources,
                acceptedBy:
                  classificationTarget === TimeSignalClassification.Accepted
                    ? TimeSignalAcceptedBy.User
                    : undefined
              }
            }
          },
          update: isTimeSignalFederatedSuggestionsEnabled
            ? updateCacheWithMergeUpdate(
                param,
                selectedClassification,
                classificationTarget,
                searchCriteria,
                sortOrder
              )
            : updateCache(
                param,
                selectedClassification,
                classificationTarget,
                searchCriteria,
                sortOrder
              ),
          awaitRefetchQueries: false,
          refetchQueries: ['timeSignalsCount', 'timeSignalsTotals']
        });

        if (
          isTimeSignalFederatedSuggestionsEnabled &&
          result.data?.mergeTimeSignals.timeSignal.id &&
          me?.id &&
          result.data?.mergeTimeSignals.timeSignal.labelValuesInferenceId
        ) {
          putSuggestions({
            variables: {
              input: {
                userUri: me.id,
                inferenceId:
                  result.data.mergeTimeSignals.timeSignal
                    .labelValuesInferenceId,
                timeSignalId: result.data.mergeTimeSignals.timeSignal.id,
                startTime: mapDateToISOString(values.startTime),
                endTime: mapDateToISOString(values.endTime),
                duration: values.duration ? values.duration : null,
                suggestions: labelValues
                  .filter(
                    (label) =>
                      Boolean(label) && label.type !== LabelType.Comment
                  )
                  .map(
                    (l: TimesheetLabel) =>
                      ({
                        labelId: l.id,
                        suggestionMetadata: l.suggestionMetadata,
                        thresholdMet: l.thresholdMet ?? false,
                        suggestedValues: l.suggestedValues ?? []
                      } as any)
                  )
              }
            },
            update: updateCacheWithFederatedSuggestions(param)
          });
        }

        if (!result.errors) {
          setToastContext({
            showSuccess: true,
            message: getMessageId(selectedClassification, classificationTarget),
            values: {
              message: truncateMessage(values.note ?? ''),
              count: `${notes.length}`
            }
          });

          if (result.data?.mergeTimeSignals.timeSignal) {
            setNotesToAnimate([
              {
                id: result.data.mergeTimeSignals.timeSignal.id,
                action: 'NotesMerge',
                folder: classificationTarget.toString() as NotesActions
              }
            ]);

            setTimeout(() => setNotesToAnimate([]), 2000);
          }
        } else {
          setToastContext({
            showError: true,
            message: 'note.merge.failure'
          });
        }
      } catch (e) {
        setToastContext({
          showError: true,
          message: 'note.merge.failure'
        });
      }

      onNoteDiscard();
      resetForm();
      setSaving(false);
    },
    [
      getValues,
      id,
      isEmptyLabelsEnabled,
      isSmartSuggestionEnabled,
      isTimeSignalFederatedSuggestionsEnabled,
      me?.id,
      mergeTimeSignals,
      notes,
      param,
      putSuggestions,
      resetForm,
      searchCriteria,
      selectedClassification,
      setNotesToAnimate,
      setToastContext,
      sortOrder
    ]
  );

  return {
    mergeAction,
    isActionInProgress: loading || putSuggestionsLoading || saving
  };
};
